import React, { useState, useEffect, useMemo } from 'react';
import { ClipboardDocumentCheckIcon, PaperClipIcon, EyeIcon } from '@heroicons/react/24/outline';
import axiosInstance from 'utils/axios';
import { useEntityDynamicFields } from 'hooks';
import { getHtmlInputType } from 'constants/fieldType.constant';
import { getAttachmentPreviewUrl, DocumentUploadFeature } from 'services/documentUploadService';
import ViewDocumentModal from 'components/leads/LeadDetails/DocumentsTab/components/ViewDocumentModal';
import { getUserPreference } from 'services/userPreferenceService';

let masterFieldsCache = null;
let masterFieldsPromise = null;

const getCachedMasterFields = async () => {
  if (masterFieldsCache) return masterFieldsCache;
  if (!masterFieldsPromise) {
    masterFieldsPromise = axiosInstance
      .get('/api/FormDesk/GetMasterFields')
      .then((response) => {
        if (response.data?.status === 'Success' && response.data?.data) {
          masterFieldsCache = JSON.parse(response.data.data);
          return masterFieldsCache;
        }
        return [];
      })
      .catch((err) => {
        console.error('Error fetching master fields:', err);
        return [];
      })
      .finally(() => {
        masterFieldsPromise = null;
      });
  }
  return masterFieldsPromise;
};

function InfoRow({ label, value, icon: Icon, isAttachment = false, files = [], onPreview, isEditor = false }) {
  return (
    <div className={`flex items-start gap-3 py-2 border-b border-gray-100 dark:border-dark-750 last:border-0 ${isEditor ? 'col-span-1 sm:col-span-2' : ''}`}>
      <div className="size-8 rounded-lg bg-gray-55 dark:bg-dark-800 flex items-center justify-center shrink-0 mt-0.5">
        {Icon && <Icon className="size-4 text-gray-500 dark:text-dark-400" />}
      </div>
      <div className="flex-1 min-w-0">
        <span className="block text-[10px] font-semibold text-gray-400 dark:text-dark-400">{label}</span>
        {isAttachment && files.length > 0 ? (
          <div className="flex flex-wrap gap-1.5 mt-1">
            {files.map((file, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => onPreview && onPreview({ ...file, feature: DocumentUploadFeature.ACTIVITY_ATTACHMENT })}
                className="inline-flex items-center gap-1 px-2 py-1 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 hover:border-primary-500 rounded-md text-xs text-gray-800 dark:text-gray-200 font-medium transition-colors cursor-pointer"
                title="Click to preview/download"
              >
                <PaperClipIcon className="size-3 text-primary-500" />
                <span className="truncate max-w-[150px]">{file.fileName}</span>
                <EyeIcon className="size-3 text-gray-400 hover:text-primary-500 ml-0.5" />
              </button>
            ))}
          </div>
        ) : isEditor && value && value !== '—' ? (
          <div
            className="text-sm text-gray-900 dark:text-white prose prose-sm dark:prose-invert max-w-none mt-1"
            dangerouslySetInnerHTML={{ __html: value }}
          />
        ) : (
          <span className="text-sm font-semibold text-gray-900 dark:text-white truncate block">
            {value !== undefined && value !== null ? value : '—'}
          </span>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border-b border-gray-100 dark:border-dark-750 pb-5 pt-3 last:border-0 last:pb-0">
      <h4 className="text-xs font-semibold text-gray-400 dark:text-dark-400 mb-3 flex items-center gap-2">
        <div className="h-1.5 w-1.5 rounded-full bg-primary-500"></div>
        {title}
      </h4>
      {children}
    </div>
  );
}

export function EntityDynamicFieldsView({
  entityTypeCode,
  entityId,
  sectionTitle = "Other Info",
  preferencePageKey,
  preferenceTypeKey = "CustomFieldsLayout",
  stepId,
  inline = false,
}) {
  const [allMasterFields, setAllMasterFields] = useState(masterFieldsCache || []);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [stepLayout, setStepLayout] = useState(null);
  
  const { fieldValues: dynamicFields } = useEntityDynamicFields(
    entityTypeCode,
    entityId || null
  );

  useEffect(() => {
    if (entityId) {
      getCachedMasterFields().then((fields) => {
        setAllMasterFields(fields || []);
      });
    }
  }, [entityId]);

  useEffect(() => {
    let isCancelled = false;
    if (!preferencePageKey) {
      setStepLayout({});
      return;
    }
    getUserPreference(preferencePageKey, preferenceTypeKey)
      .then((saved) => {
        if (!isCancelled) {
          const layout = saved?.stepLayout || saved || {};
          setStepLayout(layout);
        }
      })
      .catch((err) => {
        console.error("Error loading step layout preference:", err);
        if (!isCancelled) setStepLayout({});
      });
    return () => {
      isCancelled = true;
    };
  }, [preferencePageKey, preferenceTypeKey, entityId]);

  const filteredDynamicFields = useMemo(() => {
    if (!dynamicFields || dynamicFields.length === 0) return [];
    if (!stepId) return dynamicFields;
    if (!stepLayout) return [];

    if (stepId === 'other_info') {
      const allOtherAssigned = new Set();
      Object.entries(stepLayout).forEach(([sId, arr]) => {
        if (sId !== 'other_info' && Array.isArray(arr)) {
          arr.forEach((n) => allOtherAssigned.add((n || '').toLowerCase()));
        }
      });
      const explicitOther = (stepLayout.other_info || []).map((n) => (n || '').toLowerCase());

      return dynamicFields.filter((fv) => {
        const name = (fv.FieldName || fv.fieldName || '').toLowerCase();
        if (explicitOther.includes(name)) return true;
        return !allOtherAssigned.has(name);
      });
    }

    const assignedNames = (stepLayout[stepId] || []).map((n) => (n || '').toLowerCase());
    const matched = dynamicFields.filter((fv) => {
      const name = (fv.FieldName || fv.fieldName || '').toLowerCase();
      return assignedNames.includes(name);
    });

    return matched.sort((a, b) => {
      const aIdx = assignedNames.indexOf((a.FieldName || a.fieldName || '').toLowerCase());
      const bIdx = assignedNames.indexOf((b.FieldName || b.fieldName || '').toLowerCase());
      return aIdx - bIdx;
    });
  }, [dynamicFields, stepId, stepLayout]);

  const enrichedDynamicFields = useMemo(() => {
    if (!filteredDynamicFields || filteredDynamicFields.length === 0) return [];
    
    return filteredDynamicFields.map(fv => {
      let label = fv.FieldName || fv.fieldName || 'Field';
      let rawValue = fv.FieldValue || fv.fieldValue || '—';
      let formattedValue = rawValue;
      let isAttachment = false;
      let isEditor = false;
      let files = [];
      const fieldId = fv.MasterFieldId || fv.masterFieldId;
      
      if (allMasterFields && allMasterFields.length > 0) {
        for (const category of allMasterFields) {
          const found = category.MasterFields?.find(
            f => f.FieldName?.toLowerCase() === fv.FieldName?.toLowerCase() || f.Id === fieldId
          );
          if (found) {
            label = found.FieldLabel || label;
            const inputType = (found.HtmlInputType || found.htmlInputType || getHtmlInputType(found.FieldTypeId || found.fieldTypeId) || '').toLowerCase();
            if (inputType === 'checkbox' && (formattedValue === true || formattedValue === 'true' || formattedValue === 'false' || formattedValue === false)) {
              formattedValue = (formattedValue === true || formattedValue === 'true') ? 'Yes' : 'No';
            }
            if (inputType === 'attachments' || inputType === 'file') {
              isAttachment = true;
            }
            if (inputType === 'editor' || inputType === 'texteditor' || Number(found.FieldTypeId || found.fieldTypeId) === 11) {
              isEditor = true;
            }
            break;
          }
        }
      }

      // Check if value is JSON array of attachments regardless of inputType
      if (typeof rawValue === 'string' && rawValue.trim().startsWith('[')) {
        try {
          const parsed = JSON.parse(rawValue);
          if (Array.isArray(parsed) && parsed.length > 0 && (parsed[0].filePath || parsed[0].fileName || parsed[0].fileKey)) {
            isAttachment = true;
            files = parsed.map(f => ({
              ...f,
              name: f.fileName || f.name || f.filePath?.split('/').pop() || 'Attachment',
              fileName: f.fileName || f.name || f.filePath?.split('/').pop() || 'Attachment',
              filePath: f.filePath || f.path || f.FileUrl || f.fileKey,
              FileName: f.fileName || f.name || f.filePath?.split('/').pop() || 'Attachment', // for ViewDocumentModal compatibility
              fileSizeBytes: f.fileSizeKB ? (f.fileSizeKB * 1024) : (f.size || f.FileSize || 0),
            })).filter(f => f.filePath);
          }
        } catch (e) {
          // not JSON array
        }
      }
      
      let displayValue = formattedValue;
      if (
        displayValue === null ||
        displayValue === undefined ||
        String(displayValue).trim() === "" ||
        String(displayValue).trim() === "-" ||
        String(displayValue).trim() === "[]"
      ) {
        displayValue = "—";
      }

      return {
        id: fieldId,
        label,
        value: displayValue,
        isAttachment,
        files,
        isEditor
      };
    }).filter(field => !(field.isAttachment && (!field.files || field.files.length === 0)));
  }, [filteredDynamicFields, allMasterFields]);

  if (enrichedDynamicFields.length === 0) return null;

  if (inline) {
    return (
      <>
        {enrichedDynamicFields.map((field) => (
          <InfoRow
            key={field.id}
            label={field.label}
            value={field.value}
            icon={field.isAttachment ? PaperClipIcon : ClipboardDocumentCheckIcon}
            isAttachment={field.isAttachment}
            files={field.files}
            isEditor={field.isEditor}
            onPreview={setPreviewDocument}
          />
        ))}
        {previewDocument && (
          <ViewDocumentModal
            isOpen={true}
            onClose={() => setPreviewDocument(null)}
            document={previewDocument}
            entityType={null}
          />
        )}
      </>
    );
  }

  return (
    <Section title={sectionTitle}>
      <div className="bg-gray-55/40 dark:bg-dark-900/40 rounded-xl p-4 border border-gray-200/50 dark:border-dark-750 shadow-2xs">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
          {enrichedDynamicFields.map((field) => (
            <InfoRow
              key={field.id}
              label={field.label}
              value={field.value}
              icon={field.isAttachment ? PaperClipIcon : ClipboardDocumentCheckIcon}
              isAttachment={field.isAttachment}
              files={field.files}
              isEditor={field.isEditor}
              onPreview={setPreviewDocument}
            />
          ))}
        </div>
      </div>

      {previewDocument && (
        <ViewDocumentModal
          isOpen={true}
          onClose={() => setPreviewDocument(null)}
          document={previewDocument}
          entityType={null}
        />
      )}
    </Section>
  );
}
